import React, { ReactNode } from 'react';
import Typography from '@components/typography';
import { useDeviceDetect } from '@hooks/usedevicedetect';
import Icon from '@components/icon/icon';

import '@styles/quickdetails.scss';
import { VehicleQuickDetails } from 'interface';

interface QuickDetailsProps {
  /**
   * Cypress data attribute for tests
   */
  'data-cy': string;

  /**
   * React Testing Library data attribute for tests
   */
  'data-testid': string;

  /**
   * Quick Details object
   */
  vehicle: VehicleQuickDetails;
}

/**
 * The quick details component
 * @param dataCy Cypress test name
 * @param dataTestid Unit test name
 * @param vehicle Vehicle objects with quick details
 * @returns The quick details component
 */
const QuickDetails: React.FC<QuickDetailsProps> = ({
  'data-cy': dataCy,
  'data-testid': dataTestid,
  vehicle,
}): JSX.Element => {
  const { isMobile } = useDeviceDetect();

  // Helper to convert fuel code to human readable text
  const getFuelType = (fuelType: string): string | null => {
    switch (fuelType) {
      case 'P':
        return 'Petrol';
      case 'D':
        return 'Diesel';
      case 'E':
        return 'Electric';
      case 'H':
        return 'Hybrid';
      case 'X':
        return 'Plug-in Hybrid';
      default:
        return null;
    }
  };

  // Helper to change the transmission (or any other single-word string) to sentance case
  const toSentanceCase = (text: string): string => {
    return text
      ? text.slice(0, 1).toUpperCase() + text.slice(1).toLowerCase()
      : '';
  };

  const validateEngineSize = (engineSize: string) => {
    return engineSize && !isNaN(Number(engineSize)) && Number(engineSize) > 0;
  };

  const QuickDetailsElement = (
    value: string | number | null,
    icon: ReactNode,
    index: number
  ): JSX.Element => {
    // If the value is null, don't render this element
    if (value === null) {
      return <></>;
    }

    // Otherwise, render the component.
    return (
      <li
        key={`quick-details-list-${index}`}
        className="quick-details__element"
        data-cy={`${dataCy}-element`}
      >
        <div
          className="quick-details__icon-element"
          data-cy={`${dataCy}-element-icon-${index}`}
        >
          {icon}
        </div>
        <div
          className="quick-details__typography-element"
          data-cy={`${dataCy}-element-label-${index}`}
        >
          <Typography
            variant="body1"
            size={isMobile ? 'sm' : 'rg'}
            weight="semi-bold"
          >
            {value}
          </Typography>
        </div>
      </li>
    );
  };

  // Mapping each piece of data to it's respective icon
  const quickDetails = [
    {
      value: `${vehicle.doors} Doors`,
      icon: (
        <Icon
          classNames="quick-details-icon"
          data-cy="doors"
          data-test="doors"
          name="icon-quickdetails-doors"
          title="Doors"
        />
      ),
    },
    {
      value: toSentanceCase(vehicle.transmission),
      icon: (
        <Icon
          classNames="quick-details-icon"
          data-cy="transmission"
          data-test="transmission"
          name="icon-quickdetails-transmission"
          title="Transmission"
        />
      ),
    },
    {
      value: validateEngineSize(vehicle.engineSize)
        ? `${vehicle.engineSize} Litre`
        : null,
      icon: (
        <Icon
          classNames="quick-details-icon"
          data-cy="engine"
          data-test="engine"
          name="icon-quickdetails-engine"
          title="Engine size"
        />
      ),
    },
    {
      value: getFuelType(vehicle.fuel),
      icon: (
        <Icon
          classNames="quick-details-icon"
          data-cy="fuel"
          data-test="fuel"
          name="icon-quickdetails-fuel"
          title="Fuel type"
        />
      ),
    },
    {
      value: `${vehicle.seats} Seats`,
      icon: (
        <Icon
          classNames="quick-details-icon"
          data-cy="seats"
          data-test="seats"
          name="icon-quickdetails-seats"
          title="Seats"
        />
      ),
    },
    {
      value: vehicle.range && `${vehicle.range} Miles`,
      icon: (
        <Icon
          classNames="quick-details-icon"
          data-cy="range"
          data-test="range"
          name="icon-quickdetails-range"
          title="Range"
        />
      ),
    },
  ];

  return (
    <div
      className="quick-details"
      data-cy={`${dataCy}`}
      data-testid={`${dataTestid}`}
    >
      <div
        className="quick-details__container"
        data-cy={`${dataCy}-container`}
        data-testid={`${dataTestid}-container`}
      >
        <div
          className="quick-details__header"
          data-cy={`${dataCy}-header`}
          data-testid={`${dataTestid}-header`}
        >
          <Typography variant="h2" size="lg" weight="bold">
            Quick Details
          </Typography>
        </div>
        <ul
          className="quick-details__content-list"
          data-cy={`${dataCy}-content-list`}
          data-testid={`${dataTestid}-content-list`}
        >
          {quickDetails.map((element, index) =>
            QuickDetailsElement(element.value, element.icon, index)
          )}
        </ul>
      </div>
    </div>
  );
};

export default QuickDetails;
